import { useEffect, useState, useRef, useCallback } from "react";
import api from "../../services/api";
import { useDictionary } from "../../context/useDictionary";
import { Modal } from "@tw/tw-common-js";

export default function UserEdit({ userInit, close, isProfile, onProfileImgUpdate }) {
  const [user] = useState({ ...userInit });
  const [employee, setEmployee] = useState(null);
  const { getDictionaryValue } = useDictionary();
  const fileInputRef = useRef(null);

  // Profile image state
  const [profileImgUrl, setProfileImgUrl] = useState(null);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [imgError, setImgError] = useState(null);

  const loadProfileImg = useCallback(
    async (hrCode) => {
      const res = await api.getProfileImgThumb(hrCode);
      if (res.status === 200 && res.data) {
        const url = URL.createObjectURL(res.data);
        setProfileImgUrl(url);
        onProfileImgUpdate?.(url);
      }
    },
    [onProfileImgUpdate]
  );

  // Fetch the full employee entity by HR code
  useEffect(() => {
    async function fetchEmployee() {
      if (user._employee) {
        const res = await api.getUserEmployeeInfo(user._employee);
        if (res.status === 200) setEmployee(res.data);
      }
    }
    fetchEmployee();
  }, [user._employee]);

  // Load profile image when employee is ready
  useEffect(() => {
    if (isProfile && employee?.hrCode) {
      loadProfileImg(employee.hrCode);
    }
  }, [isProfile, employee?.hrCode, loadProfileImg]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImg(true);
    setImgError(null);
    const res = await api.uploadProfileImg(file);
    if (res.status === 200) {
      await loadProfileImg(employee.hrCode);
    } else {
      setImgError(res.data?.error || "Upload failed.");
    }
    setUploadingImg(false);
    e.target.value = "";
  };

  // Remove create logic and make all fields read-only except role (if allowed)
  return (
    <Modal
      close={close}
      title={isProfile ? "My Profile" : "User Info"}
      footer={
        <div className="btn-bar-right">
          <button className="link" onClick={close}>
            Close
          </button>
        </div>
      }
    >
      <div className="modal-user-edit">
        {isProfile && (
          <div className="profile-img-section">
            <div
              className="profile-img-avatar"
              title="Click to change profile picture"
              onClick={() => fileInputRef.current?.click()}
            >
              {profileImgUrl ? (
                <img src={profileImgUrl} alt="Profile" />
              ) : (
                <span>
                  {employee?.firstName?.charAt(0)}
                  {employee?.lastName?.charAt(0)}
                </span>
              )}
              <div className="profile-img-overlay">
                {uploadingImg ? (
                  <i className="material-icons rotating">autorenew</i>
                ) : (
                  <i className="material-icons">photo_camera</i>
                )}
              </div>
            </div>
            {imgError && <span className="error-message">{imgError}</span>}
            <input type="file" ref={fileInputRef} style={{ display: "none" }} accept="image/*" onChange={handleFileChange} />
          </div>
        )}

        <h2>Person Info</h2>
        <div className="label-row">
          <label>HR Code</label>
          <input value={employee?.hrCode || user._employee || ""} readOnly />
        </div>
        <div className="label-row">
          <label>First Name</label>
          <input value={employee?.firstName || ""} readOnly />
        </div>
        <div className="label-row">
          <label>Last Name</label>
          <input value={employee?.lastName || ""} readOnly />
        </div>
        <div className="label-row">
          <label>TW Office</label>
          <input value={getDictionaryValue("TW Offices", employee?._twOffice) || ""} readOnly />
        </div>
        <div className="label-row">
          <label>Position</label>
          <input value={getDictionaryValue("Positions", employee?._position) || ""} readOnly />
        </div>
        <div className="label-row">
          <label>Team</label>
          <input value={employee?.Team?.name || ""} readOnly />
        </div>

        <h2>Security</h2>
        <div className="label-row">
          <label>Roles</label>
          {user.roles?.map((r) => (
            <div key={r.id} className="badge">
              {r.name}
            </div>
          ))}
        </div>
        <div className="label-row">
          <label>Status</label>
          <input value={user.isArchived ? "Archived" : "Active"} readOnly />
        </div>
      </div>
    </Modal>
  );
}
